export enum BackofficeRoutes {
  ROOT = "/backoffice",
  COMPANIES = "/backoffice/companies",
  MESSAGE_MANAGER = "/backoffice/message-manager",
  CONCILIATION = "/backoffice/conciliation",
  CONTRACTS = "/backoffice/contracts",
  FINANCE_METRICS = "/backoffice/finance-metrics",
  FINANCE_APPROVALS = "/backoffice/finance-approvals",
  QUEUE = "/backoffice/queue",
  SEND_WINDOW = "/backoffice/send-window",
  TEMPLATE_PROBLEMS = "/backoffice/template-problems",
  SCHEDULE_REMINDER = "/backoffice/schedule-reminder",
  MARKETING = "/backoffice/marketing",
  MARKETING_LINK = "/backoffice/marketing/:slug",
  // Fora do prefixo /backoffice/marketing de propósito: esse path é
  // reservado à listagem/detalhe do encurtador de link (BackofficeSidebar
  // marca "Encurtador de link" como ativo por startsWith nesse prefixo).
  AB_TESTS = "/backoffice/ab-tests",
  CHATS = "/backoffice/chats",
  // Tela única com abas (?aba=): posts, pautas, sugestões, clusters,
  // analytics e CMO. Monte o link de uma aba com `blogTabPath`.
  BLOG = "/backoffice/blog",
  // Endereços antigos de quando cada aba era uma tela: só redirecionam para
  // `BLOG?aba=...`, para não quebrar favorito nem link já enviado. Declarados
  // ANTES de BLOG_EDIT de propósito: segmento fixo vence segmento dinâmico no
  // ranqueamento do React Router, e ler a ordem aqui evita a dúvida de
  // "/backoffice/blog/pautas" cair no editor com id "pautas".
  BLOG_LEGACY_CLUSTERS = "/backoffice/blog/clusters",
  BLOG_LEGACY_TOPICS = "/backoffice/blog/pautas",
  BLOG_LEGACY_SUGGESTIONS = "/backoffice/blog/sugestoes",
  BLOG_LEGACY_CMO = "/backoffice/blog/cmo",
  BLOG_LEGACY_ANALYTICS = "/backoffice/blog/analytics",
  BLOG_EDIT = "/backoffice/blog/:id",
  TEMPLATES = "/backoffice/templates",
  PASSWORD_VAULT = "/backoffice/password-vault",
  CRM_LEADS = "/backoffice/crm-leads",
  CRM_CS = "/backoffice/crm-cs",
  CRM_OPERATION = "/backoffice/crm-operation",
  FEATURE_FLAGS = "/backoffice/feature-flags",
  DASHBOARD_BANNER = "/backoffice/dashboard-banner",
  CREDITS = "/backoffice/credits",
  LINX_INTEGRATION = "/backoffice/linx-integration",
  GOOGLE_SHEET_LEAD_MONITOR = "/backoffice/google-sheet-lead-monitor",
  AUDIT_LOGS = "/backoffice/audit-logs",
  SIGN_IN_LOCK = "/backoffice/trava-de-login",
  FIDEXA_NOTIFICATIONS = "/backoffice/fidexa-notifications",
  FLOW_PAYMENTS = "/backoffice/fidexa-pay",
  FLOW_PAYMENTS_AUDIT = "/backoffice/fidexa-pay/auditoria",
  FLOW_PAYMENTS_QUEUE = "/backoffice/fidexa-pay/queue",
  FIDEXA_IA = "/backoffice/fidexa-ia",
  FIDEXA_IA_AUDIT = "/backoffice/fidexa-ia/auditoria",
  FIDEXA_IA_AGREEMENTS = "/backoffice/fidexa-ia/acordos",
  FIDEXA_IA_COSTS = "/backoffice/fidexa-ia/gastos",
  FLUKE_NUMBERS = "/backoffice/lines",
  PRODUCT_ANALYSIS_USERS = "/backoffice/product-analysis/users",
  PRODUCT_ANALYSIS_MESSAGES = "/backoffice/product-analysis/messages",
  PRODUCT_ANALYSIS_BILLING = "/backoffice/product-analysis/billing-results",
  PRODUCT_ANALYSIS_BILLING_GROUP = "/backoffice/product-analysis/billing-results/:groupId",
  PRODUCT_ANALYSIS_FIDEXA_PAY = "/backoffice/product-analysis/fidexa-pay",
  TRAINING = "/backoffice/trainings",
  TRAINING_COMMENTS = "/backoffice/trainings/comments",
  TRAINING_RANKING = "/backoffice/trainings/ranking",
  TRAINING_RAFFLE = "/backoffice/trainings/raffle",
  DESIGN_SYSTEM = "/backoffice/design-system",
  DESIGN_SYSTEM_ITEM = "/backoffice/design-system/:slug",
  KNOWLEDGE_ARTICLES = "/backoffice/knowledge/articles",
  KNOWLEDGE_ARTICLE_EDIT = "/backoffice/knowledge/articles/:id",
  KNOWLEDGE_CATEGORIES = "/backoffice/knowledge/categories",
  KNOWLEDGE_MEDIA = "/backoffice/knowledge/media",
  KNOWLEDGE_INSIGHTS = "/backoffice/knowledge/insights",
  KNOWLEDGE_AI_SUPPORT = "/backoffice/knowledge/ai-support",
  HERMES = "/backoffice/hermes",
  HERMES_META_ACCOUNTS = "/backoffice/hermes/meta-accounts",
  HERMES_JOBS = "/backoffice/hermes/jobs",
  HERMES_TEMPLATE_MESSAGES_QUEUE = "/backoffice/hermes/template-messages-queue",
  HERMES_WHATSAPP_MESSAGES = "/backoffice/hermes/whatsapp-messages",
  HERMES_WEBHOOK_SEARCH = "/backoffice/hermes/webhooks",
  HERMES_TEMPLATES = "/backoffice/hermes/templates",
  HERMES_TEMPLATE_LIBRARY = "/backoffice/hermes/template-library",
  HERMES_MESSAGE_PRICES = "/backoffice/hermes/message-prices",
  HERMES_BILLING = "/backoffice/hermes/billing",
  HERMES_BILLING_ACCOUNT = "/backoffice/hermes/billing/:accountId",
  HERMES_TEMPLATE_MESSAGE_QUEUE_DETAIL = "/backoffice/hermes/template-messages-queue/:id",
}

/** Detalhe de uma intenção da Fila de Disparo: o resumo e as mensagens de cada envio. */
export const hermesTemplateMessageQueueDetailPath = (id: string): string =>
  `${BackofficeRoutes.HERMES_TEMPLATE_MESSAGES_QUEUE}/${id}`;

/** Relatório de cobrança de uma conta WhatsApp Business, mantendo o período escolhido (`search` sem o `?`). */
export const hermesBillingAccountPath = (
  accountId: string,
  search = ""
): string =>
  `${BackofficeRoutes.HERMES_BILLING}/${accountId}${search ? `?${search}` : ""}`;

/** Monta a rota de detalhe de um link a partir do slug. */
export const marketingLinkPath = (slug: string): string =>
  `${BackofficeRoutes.MARKETING}/${slug}`;

/** Abas da página Fidexa Pay do backoffice (`?aba=`; "webhooks" é a padrão). */
export const FIDEXA_PAY_TABS = [
  "webhooks",
  "gateway",
  "saques",
  "sandbox",
  "chaves",
  "movimentacoes",
  "consultas-linx",
] as const;

export type FidexaPayTab = (typeof FIDEXA_PAY_TABS)[number];

/** Abas da tela Linhas do backoffice (`?aba=`; "linhas" é a padrão). */
export const LINES_TABS = ["linhas", "chips", "webhook", "logs"] as const;

export type LinesTab = (typeof LINES_TABS)[number];

/** Abas da tela do blog no Backoffice, na ordem em que aparecem. */
export const BLOG_TABS = [
  "posts",
  "pautas",
  "sugestoes",
  "clusters",
  "analytics",
  "cmo",
] as const;

export type BlogTab = (typeof BLOG_TABS)[number];

/** Monta o link de uma aba do blog ("posts" é a aba padrão, sem parâmetro). */
export const blogTabPath = (aba: BlogTab): string =>
  aba === "posts"
    ? BackofficeRoutes.BLOG
    : `${BackofficeRoutes.BLOG}?aba=${aba}`;

/** Monta a rota de edição de um post ("new" para criação). */
export const blogEditPath = (id: string): string =>
  `${BackofficeRoutes.BLOG}/${id}`;

/** Billing results of one company group, keeping the chosen period. */
export const billingResultsGroupPath = (groupId: string, search = ""): string =>
  `${BackofficeRoutes.PRODUCT_ANALYSIS_BILLING}/${groupId}${search ? `?${search}` : ""}`;

/** Knowledge article editor route ("new" to create). */
export const knowledgeArticleEditPath = (id: string): string =>
  `${BackofficeRoutes.KNOWLEDGE_ARTICLES}/${id}`;
